import { useNaphthaleneStore } from '../store/useNaphthaleneStore';

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

async function waitFor(predicate: () => boolean, timeoutMs = 60000, intervalMs = 100) {
  const start = Date.now();
  while (!predicate()) {
    if (!useNaphthaleneStore.getState().isAutoDemoRunning) return false;
    if (Date.now() - start > timeoutMs) return false;
    await sleep(intervalMs);
  }
  return true;
}

export async function runAutoDemo() {
  const get = () => useNaphthaleneStore.getState();
  if (get().isAutoDemoRunning) return;

  get().reset();
  get().setAutoDemoRunning(true);
  get().setSimulationSpeed(10);

  let recordInterval: ReturnType<typeof setInterval> | null = null;

  try {
    get().addNaphthaleneToTube();
    await waitFor(() => !get().isAddingNaphthalene);
    if (!get().isAutoDemoRunning) return;
    await sleep(200);

    get().insertThermometer();
    await waitFor(() => !get().isInsertingThermometer);
    if (!get().isAutoDemoRunning) return;
    await sleep(200);

    get().pourWater();
    await waitFor(() => !get().isPouringWater);
    if (!get().isAutoDemoRunning) return;
    await sleep(200);

    get().immerseTube();
    await waitFor(() => !get().isImmersingTube);
    if (!get().isAutoDemoRunning) return;
    await sleep(200);

    // Heat
    get().toggleHeating();

    // Auto-record temperature every 3s real-time (~30s sim time at 10×)
    recordInterval = setInterval(() => {
      const s = get();
      const recordable =
        s.labStage === 'heating' || s.labStage === 'melting' ||
        s.labStage === 'transfer-ready' || s.labStage === 'cooling' ||
        s.labStage === 'freezing';
      if (recordable) s.recordReading();
    }, 3000);

    await waitFor(() => get().labStage === 'transfer-ready', 90000);
    if (!get().isAutoDemoRunning) return;

    // Transfer to conical flask
    get().transferToFlask();
    await waitFor(() => !get().isTransferring);
    if (!get().isAutoDemoRunning) return;

    // Stir during cooling to prevent supercooling
    get().setStirring(true);
    await waitFor(() => get().labStage === 'completed', 90000);
  } finally {
    if (recordInterval) clearInterval(recordInterval);
    const s = useNaphthaleneStore.getState();
    s.setAutoDemoRunning(false);
    s.setSimulationSpeed(1);
    s.setStirring(false);
  }
}

export function stopAutoDemo() {
  useNaphthaleneStore.getState().setAutoDemoRunning(false);
}
